import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { TrendingUp, Info } from 'lucide-react';

const SalesSpendCorrelation = () => {
  const [selectedView, setSelectedView] = useState('country'); // 'country' or 'channel'

  const countryData = [
    { name: 'UAE', flag: '🇦🇪', spend: 6300000, salesGrowth: 18.52, spendGrowth: 12.0, color: 'bg-emerald-500', darkColor: 'bg-emerald-600' },
    { name: 'Saudi Arabia', flag: '🇸🇦', spend: 6120000, salesGrowth: 15.23, spendGrowth: 10.2, color: 'bg-blue-500', darkColor: 'bg-blue-600' },
    { name: 'Kuwait', flag: '🇰🇼', spend: 1440000, salesGrowth: 12.84, spendGrowth: 8.4, color: 'bg-purple-500', darkColor: 'bg-purple-600' },
    { name: 'Qatar', flag: '🇶🇦', spend: 1620000, salesGrowth: 16.15, spendGrowth: 9.1, color: 'bg-orange-500', darkColor: 'bg-orange-600' },
    { name: 'Bahrain', flag: '🇧🇭', spend: 1080000, salesGrowth: 14.37, spendGrowth: 7.3, color: 'bg-teal-500', darkColor: 'bg-teal-600' },
    { name: 'Egypt', flag: '🇪🇬', spend: 720000, salesGrowth: 11.72, spendGrowth: 5.6, color: 'bg-red-500', darkColor: 'bg-red-600' },
    { name: 'Morocco', flag: '🇲🇦', spend: 540000, salesGrowth: 9.28, spendGrowth: 4.2, color: 'bg-cyan-500', darkColor: 'bg-cyan-600' }
  ];

  const channelData = [
    { name: 'Digital', spend: 4800000, salesGrowth: 22.15, spendGrowth: 15.0, color: 'bg-violet-500', darkColor: 'bg-violet-600' },
    { name: 'TV', spend: 5200000, salesGrowth: 16.84, spendGrowth: 8.5, color: 'bg-indigo-500', darkColor: 'bg-indigo-600' },
    { name: 'Outdoor', spend: 2800000, salesGrowth: 13.47, spendGrowth: 6.8, color: 'bg-emerald-500', darkColor: 'bg-emerald-600' },
    { name: 'Print', spend: 2100000, salesGrowth: 9.23, spendGrowth: 3.2, color: 'bg-amber-500', darkColor: 'bg-amber-600' },
    { name: 'Radio', spend: 1950000, salesGrowth: 11.56, spendGrowth: 5.1, color: 'bg-rose-500', darkColor: 'bg-rose-600' }
  ];

  const currentData = selectedView === 'country' ? countryData : channelData;
  const maxX = Math.max(...currentData.map(d => d.spendGrowth));
  const maxY = Math.max(...currentData.map(d => d.salesGrowth));

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyInMillions = (amount) => {
    const millions = amount / 1000000;
    return `$${millions.toFixed(1)}M`;
  };

  return (
    <Card className="shadow-lg border-l-4 border-l-emerald-500">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-3">
              Sales Growth vs Marketing Spend Growth
            </CardTitle>
            
            {/* Legends */}
            <div className="flex flex-wrap gap-2 mb-4">
              {currentData.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                  <span className="text-xs font-medium text-gray-700">
                    {selectedView === 'country' && item.flag} {item.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex space-x-2">
            <Button
              variant={selectedView === 'country' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedView('country')}
              className={selectedView === 'country' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              By Country
            </Button>
            <Button
              variant={selectedView === 'channel' ? 'default' : 'outline'}
              size="sm" 
              onClick={() => setSelectedView('channel')}
              className={selectedView === 'channel' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              By Channel
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Scatterplot */}
          <div className="relative">
            <div className="h-80 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-4 relative overflow-hidden border border-emerald-200">
              {/* Grid lines */}
              <svg className="absolute inset-0 w-full h-full">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e5e7eb" strokeWidth="1" opacity="0.5"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
              
              {/* Data points */}
              {currentData.map((point, index) => {
                const xPos = (point.spendGrowth / maxX) * 80 + 10; // 10% margin for proper axis alignment
                const yPos = 90 - (point.salesGrowth / maxY) * 80; // Inverted Y, use salesGrowth for y-axis
                
                return (
                  <div
                    key={index}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
                    style={{ left: `${xPos}%`, top: `${yPos}%` }}
                  >
                    <div className={`w-4 h-4 ${point.color} rounded-full shadow-lg hover:scale-150 transition-all duration-200 opacity-80 hover:opacity-100`}></div>
                    
                    {/* Tooltip */}
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                      <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap shadow-lg">
                        <div className="font-semibold flex items-center space-x-1">
                          {selectedView === 'country' && <span>{point.flag}</span>}
                          <span>{point.name}</span>
                        </div>
                        <div>Spend: {formatCurrency(point.spend)}</div>
                        <div>Growth: {point.salesGrowth.toFixed(2)}%</div>
                        <div>Spend Growth: {point.spendGrowth.toFixed(2)}%</div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Trend line (simplified linear) */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none">
                <line 
                  x1="5%" y1="80%" 
                  x2="90%" y2="20%" 
                  stroke="#10b981" 
                  strokeWidth="2" 
                  strokeDasharray="5,5" 
                  opacity="0.7"
                />
              </svg>
            </div>
            
            {/* X-axis labels */}
            <div className="flex justify-between items-center mt-4 px-8">
              <span className="text-xs text-gray-600">0%</span>
              <span className="text-xs text-gray-600">{(maxX * 0.25).toFixed(0)}%</span>
              <span className="text-xs text-gray-600">{(maxX * 0.5).toFixed(0)}%</span>
              <span className="text-xs text-gray-600">{(maxX * 0.75).toFixed(0)}%</span>
              <span className="text-xs text-gray-600">{Math.ceil(maxX)}%</span>
            </div>
            <div className="text-center mt-2">
              <span className="text-sm font-medium text-gray-700">
                {selectedView === 'country' ? 'Marketing Spend Growth by Country (%)' : 'Marketing Spend Growth by Channel (%)'}
              </span>
            </div>
            
            {/* Y-axis labels */}
            <div className="absolute left-0 top-4 h-72 flex flex-col justify-between py-4 -ml-12">
              <span className="text-xs text-gray-600">{Math.ceil(maxY)}%</span>
              <span className="text-xs text-gray-600">{(maxY * 0.75).toFixed(0)}%</span>
              <span className="text-xs text-gray-600">{(maxY * 0.5).toFixed(0)}%</span>
              <span className="text-xs text-gray-600">{(maxY * 0.25).toFixed(0)}%</span>
              <span className="text-xs text-gray-600">0%</span>
            </div>
            <div className="absolute left-0 top-1/2 transform -rotate-90 -translate-y-1/2 -translate-x-8">
              <span className="text-sm font-medium text-gray-700">Sales Growth (%)</span>
            </div>
          </div>

          {/* Key Insights */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Key Insights</h4>
                {selectedView === 'country' ? (
                  <div className="space-y-2 text-sm text-blue-800">
                    <p><strong>UAE:</strong> Sales growth 18.5% vs spend growth 12.0% — strong leverage and ROI.</p>
                    <p><strong>Kuwait:</strong> Sales growth 12.8% vs spend growth 8.4% — efficient uplift versus investment.</p>
                    <p><strong>Saudi Arabia:</strong> Sales growth 15.2% vs spend growth 10.2% — positive leverage; room to optimize mix.</p>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm text-blue-800">
                    <p><strong>Digital:</strong> Sales growth 22.1% vs spend growth 15.0% — best leverage; maintain momentum.</p>
                    <p><strong>TV:</strong> Sales growth 16.8% vs spend growth 8.5% — solid efficiency; monitor diminishing returns.</p>
                    <p><strong>Print:</strong> Sales growth 9.2% vs spend growth 3.2% — low absolute growth; evaluate role and ROI.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Performance Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-lg font-bold text-green-600">
                {(currentData.reduce((acc, d) => acc + d.salesGrowth, 0) / currentData.length).toFixed(2)}%
              </div>
              <div className="text-sm text-green-700">Avg Growth</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-lg font-bold text-blue-600">
                {formatCurrencyInMillions(currentData.reduce((acc, d) => acc + d.spend, 0))}
              </div>
              <div className="text-sm text-blue-700">Total Spend</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-lg font-bold text-purple-600">
                {currentData.filter(d => d.salesGrowth > 15).length}
              </div>
              <div className="text-sm text-purple-700">High Performers</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <div className="text-lg font-bold text-yellow-600">
                0.78
              </div>
              <div className="text-sm text-yellow-700">Correlation</div>
            </div>
          </div>

        </div>
      </CardContent>
    </Card>
  );
};

export default SalesSpendCorrelation;